import { CURRENT_MODE, FETCH_MODE } from "common/constants";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer
} from "react";

export const AuthContext = createContext();

export const initialState = {
  accessToken: window.localStorage.getItem("accessToken"),
  sessionId: window.localStorage.getItem("sessionId"),
  roleConfig: JSON.parse(window.localStorage.getItem("roleConfig")),
  user: JSON.parse(window.localStorage.getItem("user")),
  fetchMode: window.localStorage.getItem("fetchMode"),
  currentMode: window.localStorage.getItem("currentMode"),
  primaryUser: window.localStorage.getItem("primaryUser")
};

export function authReducer(state, action) {
  switch (action.type) {
    case "onRoleSwitch":
    case "onRefresh":
    case "onLogin": {
      const { accessToken, refreshToken, role, currentMode, primaryUser } =
        action.payload;
      const fetchMode =
        currentMode === CURRENT_MODE.purchase
          ? FETCH_MODE.supplier
          : FETCH_MODE.customer;
      window.localStorage.setItem("accessToken", accessToken);
      window.localStorage.setItem("sessionId", refreshToken);
      window.localStorage.setItem("roleConfig", JSON.stringify(role));
      window.localStorage.setItem("user", JSON.stringify(action.payload));
      window.localStorage.setItem("fetchMode", fetchMode);
      window.localStorage.setItem("currentMode", currentMode);
      window.localStorage.setItem("primaryUser", primaryUser);
      return {
        ...state,
        accessToken,
        sessionId: refreshToken,
        roleConfig: role,
        user: action.payload,
        fetchMode,
        currentMode,
        primaryUser
      };
    }
    case "onLogout": {
      window.localStorage.clear();
      return {
        accessToken: null,
        sessionId: null,
        roleConfig: null,
        user: null,
        primaryUser: null
      };
    }
    default:
      throw new Error("Invalid action type");
  }
}

export default function AuthContextProvider({ children }) {
  const [authConfig, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    global.authDispatch = dispatch;
  }, []);

  const parsedRoleConfig = useMemo(() => {
    if (authConfig.roleConfig) {
      const { transactionModes } = authConfig.roleConfig;
      const genericConfig = transactionModes.reduce((config, mode) => {
        const modeName = mode.name;
        const modeConfig = mode.features.reduce((accumulator, feature) => {
          const permissions = feature.permissionTypes.map((type) => type.name);
          const featureName = feature.name;
          return { ...accumulator, [featureName]: permissions };
        }, {});
        return { ...config, [modeName]: modeConfig };
      }, {});
      return genericConfig[authConfig.currentMode];
    }
    return null;
  }, [authConfig]);

  const authContext = useMemo(
    () => ({
      authConfig,
      parsedRoleConfig,
      dispatch
    }),
    [authConfig, parsedRoleConfig]
  );

  return (
    <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>
  );
}

export const useAuthContext = () => useContext(AuthContext);
